import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

// @mui material components
import Card from "@mui/material/Card";
import Switch from "@mui/material/Switch";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Fade from "@mui/material/Fade";
import Grow from "@mui/material/Grow";
import Zoom from "@mui/material/Zoom";
import { keyframes } from "@mui/system";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import axios from "axios";

// Icons
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import LoginIcon from "@mui/icons-material/Login";

// Images
import logo from "assets/images/logo.png";
import BASE_URL from "BASE_URL";

// Premium Animations
const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
  100% { transform: translateY(0px); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 20px rgba(198, 40, 40, 0.3); }
  50% { box-shadow: 0 0 40px rgba(198, 40, 40, 0.6); }
  100% { box-shadow: 0 0 20px rgba(198, 40, 40, 0.3); }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const bounceIn = keyframes`
  0% { transform: scale(0); opacity: 0; }
  50% { transform: scale(1.1); }
  70% { transform: scale(0.95); }
  100% { transform: scale(1); opacity: 1; }
`;

const slideInLeft = keyframes`
  from { transform: translateX(-100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(100px); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

function Basic() {
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Validation errors
  const [errors, setErrors] = useState({
    email: "",
    password: "",
  });

  const metaRed = "#C62828";
  const metaGold = "#FFD700";
  const metaBlue = "#344767";

  // Load remembered email
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  // Validation function
  const validateForm = () => {
    let isValid = true;
    const newErrors = { email: "", password: "" };

    if (!email) {
      setSnackbar({ open: true, message: "Email is required", severity: "error" });
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setSnackbar({ open: true, message: "Email is invalid", severity: "error" });
      isValid = false;
    }

    if (!password) {
      setSnackbar({ open: true, message: "Password is required", severity: "error" });
      isValid = false;
    } else if (password.length < 6) {
      setSnackbar({
        open: true,
        message: "Password must be at least 6 characters",
        severity: "error",
      });
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setLoading(true);

    try {
      const response = await axios.post(
        `${BASE_URL}/admin/login`,
        { email, password },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response.data.success) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.data));

        if (rememberMe) {
          localStorage.setItem("rememberedEmail", email);
        } else {
          localStorage.removeItem("rememberedEmail");
        }

        setSnackbar({
          open: true,
          message: response.data.message || "Login successful! Redirecting...",
          severity: "success",
        });

        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        setSnackbar({
          open: true,
          message: response.data.message || "Login failed. Please check your credentials.",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Network error. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") handleLogin();
  };

  return (
    <MDBox
      width="100vw"
      height="100vh"
      sx={{
        overflow: "hidden",
        position: "relative",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Animated Background Orbs */}
      <MDBox
        sx={{
          position: "absolute",
          width: "500px",
          height: "500px",
          background: `radial-gradient(circle, ${metaRed} 0%, transparent 70%)`,
          borderRadius: "50%",
          top: "-10%",
          right: "-5%",
          filter: "blur(100px)",
          animation: `${float} 8s ease-in-out infinite`,
          opacity: 0.3,
        }}
      />
      <MDBox
        sx={{
          position: "absolute",
          width: "400px",
          height: "400px",
          background: `radial-gradient(circle, ${metaGold} 0%, transparent 70%)`,
          borderRadius: "50%",
          bottom: "-10%",
          left: "-5%",
          filter: "blur(100px)",
          animation: `${float} 10s ease-in-out infinite reverse`,
          opacity: 0.2,
        }}
      />
      <MDBox
        sx={{
          position: "absolute",
          width: "300px",
          height: "300px",
          background: `radial-gradient(circle, #667eea 0%, transparent 70%)`,
          borderRadius: "50%",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          filter: "blur(120px)",
          animation: `${pulse} 6s ease-in-out infinite`,
          opacity: 0.4,
        }}
      />

      {/* Floating Particles */}
      {[...Array(30)].map((_, i) => (
        <MDBox
          key={i}
          sx={{
            position: "absolute",
            width: `${Math.random() * 6 + 2}px`,
            height: `${Math.random() * 6 + 2}px`,
            background: `rgba(255, 255, 255, ${Math.random() * 0.5 + 0.2})`,
            borderRadius: "50%",
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `${float} ${Math.random() * 10 + 5}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
            opacity: Math.random() * 0.5,
          }}
        />
      ))}

      {/* Main Card */}
      <Grow in={true} timeout={800}>
        <Card
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            width: "450px",
            padding: "40px",
            borderRadius: "32px",
            background: "rgba(255, 255, 255, 0.95)",
            backdropFilter: "blur(20px)",
            boxShadow: isHovered
              ? "0 30px 60px rgba(0, 0, 0, 0.3)"
              : "0 20px 40px rgba(0, 0, 0, 0.2)",
            border: "1px solid rgba(255, 255, 255, 0.3)",
            zIndex: 2,
            transition: "all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            transform: isHovered ? "translateY(-10px)" : "translateY(0)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Animated Border Gradient */}
          <MDBox
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "4px",
              background: `linear-gradient(90deg, ${metaRed}, ${metaGold}, ${metaRed})`,
              animation: `${shimmer} 3s infinite linear`,
              backgroundSize: "200% 100%",
            }}
          />

          {/* Logo Section */}
          <Zoom in={true} timeout={600}>
            <MDBox textAlign="center" mb={2}>
              <MDBox
                component="img"
                src={logo}
                alt="Logo"
                width="200px"
                sx={{
                  filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.2))",
                  transition: "all 0.3s ease",
                  animation: `${bounceIn} 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55)`,
                  "&:hover": {
                    transform: "scale(1.1) rotate(5deg)",
                  },
                }}
              />
            </MDBox>
          </Zoom>

          <Zoom in={true} timeout={600} style={{ transitionDelay: "100ms" }}>
            <MDBox textAlign="center" mb={3}>
              {/* <MDTypography
                variant="h3"
                fontWeight="bold"
                sx={{
                  background: `linear-gradient(135deg, ${metaRed} 0%, ${metaGold} 100%)`,
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  mb: 1,
                }}
              >
                MetaTrade
              </MDTypography> */}
              <MDTypography variant="body2" color="textSecondary" fontWeight="medium">
                Welcome back! Please login to your account
              </MDTypography>
            </MDBox>
          </Zoom>

          {/* Form */}
          <MDBox component="form" onKeyPress={handleKeyPress}>
            <Fade in={true} timeout={800}>
              <MDBox mb={2}>
                <MDInput
                  type="email"
                  label="Email Address"
                  fullWidth
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (errors.email) setErrors({ ...errors, email: "" });
                  }}
                  error={!!errors.email}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon sx={{ color: metaBlue }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(5px)",
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 2px ${metaRed}20`,
                      },
                    },
                  }}
                />
              </MDBox>
            </Fade>

            <Fade in={true} timeout={800} style={{ transitionDelay: "100ms" }}>
              <MDBox mb={2}>
                <MDInput
                  type={showPassword ? "text" : "password"}
                  label="Password"
                  fullWidth
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors({ ...errors, password: "" });
                  }}
                  error={!!errors.password}
                  helperText={errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon sx={{ color: metaBlue }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                          sx={{ color: metaBlue }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateX(5px)",
                      },
                      "&.Mui-focused": {
                        boxShadow: `0 0 0 2px ${metaBlue}20`,
                      },
                    },
                  }}
                />
              </MDBox>
            </Fade>

            {/* Remember Me & Forgot Password */}
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
              <MDBox display="flex" alignItems="center">
                {/* <Switch
                  checked={rememberMe}
                  onChange={handleSetRememberMe}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: metaRed,
                      "&:hover": {
                        backgroundColor: `${metaRed}20`,
                      },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: metaRed,
                    },
                  }}
                /> */}
                {/* <MDTypography
                  variant="button"
                  color="text"
                  onClick={handleSetRememberMe}
                  sx={{ cursor: "pointer", userSelect: "none", fontWeight: "medium" }}
                >
                  Remember me
                </MDTypography> */}
              </MDBox>
              <MDTypography
                component={Link}
                to="/forgot-password"
                variant="button"
                fontWeight="medium"
                sx={{
                  color: metaBlue,
                  textDecoration: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    textDecoration: "underline",
                    transform: "translateX(-3px)",
                  },
                }}
              >
                Forgot password?
              </MDTypography>
            </MDBox>

            {/* Login Button */}
            <Zoom in={true} timeout={600} style={{ transitionDelay: "200ms" }}>
              <MDButton
                variant="gradient"
                fullWidth
                onClick={handleLogin}
                disabled={loading}
                sx={{
                  // "M" ke color ka gradient (Dark Blue to Darker Blue)
                  background: `linear-gradient(135deg, #27385E 0%, #131E36 100%)`,
                  color: "#fff",
                  height: "50px",
                  borderRadius: "12px",
                  fontSize: "1rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  position: "relative",
                  overflow: "hidden",
                  transition: "all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
                  "&:hover": {
                    // Hover par thoda light blue gradient
                    background: `linear-gradient(135deg, #374E82 0%, #27385E 100%)`,
                    transform: "translateY(-2px)",
                    // Shadow ka color bhi red se hatakar blue kar diya hai
                    boxShadow: `0 8px 25px rgba(39, 56, 94, 0.5)`,
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                    transition: "left 0.5s ease",
                  },
                  "&:hover::before": {
                    left: "100%",
                  },
                }}
                startIcon={!loading && <LoginIcon />}
              >
                {loading ? (
                  <MDBox display="flex" alignItems="center" gap={1}>
                    <MDBox
                      sx={{
                        width: 20,
                        height: 20,
                        border: "2px solid #fff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        animation: `${rotate} 1s linear infinite`,
                      }}
                    />
                    Signing In...
                  </MDBox>
                ) : (
                  "Sign In"
                )}
              </MDButton>
            </Zoom>

            {/* Sign Up Link */}
            {/* <Fade in={true} timeout={800} style={{ transitionDelay: "300ms" }}>
              <MDBox mt={3} textAlign="center">
                <MDTypography variant="body2" color="textSecondary">
                  Don&lsquo;t have an account?{" "}
                  <MDTypography
                    component={Link}
                    to="/authentication/sign-up"
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      color: metaRed,
                      textDecoration: "none",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#8b0000",
                      },
                    }}
                  >
                    Create Account
                  </MDTypography>
                </MDTypography>
              </MDBox>
            </Fade> */}
          </MDBox>
        </Card>
      </Grow>

      {/* Premium Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={Fade}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          elevation={6}
          variant="filled"
          sx={{
            width: "100%",
            borderRadius: "12px",
            fontWeight: "bold",
            "& .MuiAlert-icon": {
              fontSize: "24px",
            },
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </MDBox>
  );
}

export default Basic;
